import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

const SortableItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'shadow-lg ring-2 ring-primary rounded-lg' : ''}>
      <div className="flex items-center">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-white p-2 touch-none flex-shrink-0"
          data-testid={`drag-handle-${id}`}
        >
          <GripVertical className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  );
};

/**
 * Reusable sortable list with drag-and-drop.
 * @param {Array} items - Array of items with `_id` or `id` field
 * @param {Function} onReorder - Called with new ordered array after drag
 * @param {Function} renderItem - (item, index) => JSX for each item
 * @param {string} idPrefix - Unique prefix for DnD context
 */
const SortableList = ({ items, onReorder, renderItem, idPrefix = 'sort' }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const getItemId = (item) => item._id || item.id;
  const sortableIds = items.map(item => `${idPrefix}_${getItemId(item)}`);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = String(active.id).replace(`${idPrefix}_`, '');
    const overId = String(over.id).replace(`${idPrefix}_`, '');

    const oldIndex = items.findIndex(item => getItemId(item) === activeId);
    const newIndex = items.findIndex(item => getItemId(item) === overId);
    if (oldIndex === -1 || newIndex === -1) return;

    onReorder(arrayMove(items, oldIndex, newIndex));
  };

  if (items.length === 0) return null;

  return (
    <DndContext
      id={`dnd-${idPrefix}`}
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {items.map((item, index) => (
            <SortableItem key={`${idPrefix}_${getItemId(item)}`} id={`${idPrefix}_${getItemId(item)}`}>
              {renderItem(item, index)}
            </SortableItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default SortableList;
